/* global webpack */

import angular from 'angular'

import deline from './deline'
import parseUrl from './parse-url'
import MemoizeSimple from './memoize-simple'

import { embedModal } from './shared-constants'
import { isEmpty, defaultTo } from './bits'
import { toString, escapeRegExp } from 'lodash-es'
import { toUrl, escapeHtml, htmlToText } from './security'

const {
  HOR_ALIGN_CONFIG_DEFAULT,
  VER_ALIGN_CONFIG_DEFAULT,
  MAX_WIDTH_CONFIG_DEFAULT,
  MAX_HEIGHT_CONFIG_DEFAULT
} = embedModal

angular
  .module('ggs.utils')
  .factory('postShare', postShareProvider)

function postShareProvider(env) {

  const memParseUrl = MemoizeSimple(parseUrl)
  const memHtmlToText = MemoizeSimple(htmlToText)

  const POST_KEY_PLH = ':post_key:'
  const SLIDE_INDEX_PLH = ':post_key:'
  const GALLERY_KEY_PLH = ':gallery_key:'
  const GALLERY_PUBLIC_BASE_URL_PLH = ':gallery_public_base_url:'

  const CODE_SCRIPT_SRC_TMPL = `${GALLERY_PUBLIC_BASE_URL_PLH}/webpack/gallery-v2.launcher.js`
  const CODE_SCRIPT_SRC_PLH = ':code_script_src:'
  const CODE_SCRIPT_TMPL = `<script src="${CODE_SCRIPT_SRC_PLH}" async></script>`

  const CODE_ROOT_MAX_WIDTH_PLH = ':code_root_max_width:'
  const CODE_ROOT_MAX_HEIGHT_PLH = ':code_root_max_height:'
  const CODE_ROOT_HOR_ALIGN_PLH = ':code_root_hor_align:'
  const CODE_ROOT_VER_ALIGN_PLH = ':code_root_ver_align:'

  const CODE_ROOT_TMPL = deline(`
    <div
      data-gallery-include="${GALLERY_KEY_PLH}"
      data-displayed-item="${POST_KEY_PLH}"
      data-displayed-slide="${SLIDE_INDEX_PLH}"
      data-embed-modal-max-width="${CODE_ROOT_MAX_WIDTH_PLH}"
      data-embed-modal-max-height="${CODE_ROOT_MAX_HEIGHT_PLH}"
      data-embed-modal-hor-align="${CODE_ROOT_HOR_ALIGN_PLH}"
      data-embed-modal-ver-align="${CODE_ROOT_VER_ALIGN_PLH}"
      data-require-css-hard-reset="true"
      data-required-extensions="ggsExtensionEmbedModal"></div>`)

  const CODE_SCRIPT_PLH = ':code_script:'
  const CODE_ROOT_PLH = ':code_root:'
  const CODE_TMPL = `${CODE_ROOT_PLH}${CODE_SCRIPT_PLH}`

  const EMBED_ITEM_RE = /[?&]d=\w+/g
  const EMBED_SLIDE_RE = /[?&]s=\w+/g
  const EMBED_ITEM_KEY = 'd'
  const SMART_ROOT_KEY = 'u'
  const EMBED_SLIDE_INDEX = 'i'

  const GLEAM_PATH_SIGN = 'g'
  const SMART_PATH_SIGN = 'sg'
  const FACEBOOK_PATH_SIGN = 'fbg'

  // fmtText
  function fmtText(properties) {
    return memHtmlToText(properties.header_display_name)
  }

  // fmtCode
  function fmtCode(config, post, properties, getSlideIndex) {
    const postKey = post.id.toString(36)
    const slideIndex = getSlideIndex()

    const formatter = FmtCode(webpack.galleryAssetHost, properties.key)
    return formatter(config, postKey, slideIndex)
  }

  const FmtCode = MemoizeSimple((gallery_public_base_url, key) => {
    const scriptSrc = toUrl(CODE_SCRIPT_SRC_TMPL.replace(GALLERY_PUBLIC_BASE_URL_PLH, gallery_public_base_url))
    const script = CODE_SCRIPT_TMPL.replace(CODE_SCRIPT_SRC_PLH, escapeHtml(scriptSrc))
    const rootTmpl = CODE_ROOT_TMPL.replace(GALLERY_KEY_PLH, escapeHtml(key))
    const sumTmpl = CODE_TMPL
      .replace(CODE_SCRIPT_PLH, script)
      .replace(CODE_ROOT_PLH, rootTmpl)

    return function codeFormatter(config, postKey, slideIndex) {
      const postKeyAttr = escapeHtml(postKey)
      const slideIndexAttr = escapeHtml(toString(slideIndex))

      const horAlignAttr = escapeHtml(defaultTo(config.horAlign, HOR_ALIGN_CONFIG_DEFAULT))
      const verAlignAttr = escapeHtml(defaultTo(config.verAlign, VER_ALIGN_CONFIG_DEFAULT))
      const maxWidthAttr = escapeHtml(defaultTo(config.maxWidth, MAX_WIDTH_CONFIG_DEFAULT))
      const maxHeightAttr = escapeHtml(defaultTo(config.maxHeight, MAX_HEIGHT_CONFIG_DEFAULT))

      return sumTmpl
        .replace(POST_KEY_PLH, postKeyAttr)
        .replace(SLIDE_INDEX_PLH, slideIndexAttr)
        .replace(CODE_ROOT_HOR_ALIGN_PLH, horAlignAttr)
        .replace(CODE_ROOT_VER_ALIGN_PLH, verAlignAttr)
        .replace(CODE_ROOT_MAX_WIDTH_PLH, maxWidthAttr)
        .replace(CODE_ROOT_MAX_HEIGHT_PLH, maxHeightAttr)
    }
  })

  // fmtLink
  function fmtLink(post, properties, getSlideIndex) {
    const postKey = post.id.toString(36)
    const slideIndex = getSlideIndex()

    const formatter = FmtLink(
      webpack.apiServerHost,
      properties.root_location,
      properties.key,
      env.isEmbedInCommonSite,
      properties.facebook_tab,
      properties.force_hosted_share_link,
      properties.force_embedded_share_link)

    return formatter(postKey, slideIndex)
  }

  const FmtLink = MemoizeSimple((
    base_url,
    root_location,
    key,
    embed_in_common_site,
    facebook_tab,
    force_hosted_share_link,
    force_embedded_share_link) => {

    let fmtLink = null
    if (force_hosted_share_link) {
      fmtLink = FmtGleamLink(base_url, root_location, key)
    } else if (force_embedded_share_link) {
      fmtLink = FmtEmbedLink(root_location)
    } else if (facebook_tab) {
      fmtLink = FmtFacebookLink(base_url, key)
    } else if (embed_in_common_site) {
      fmtLink = FmtSmartLink(base_url, root_location, key)
    } else {
      fmtLink = FmtGleamLink(base_url, root_location, key)
    }

    return fmtLink
  })

  // FmtEmbedLink
  function FmtEmbedLink(root_location) {
    const slideTmpl = fmtEmbedTmpl(root_location, true)
    const plainTmpl = fmtEmbedTmpl(root_location)

    return (postKey, slideIndex) => {
      const tmpl = slideIndex !== 0
        ? slideTmpl
        : plainTmpl

      return toUrl(tmpl.replace(POST_KEY_PLH, postKey).replace(SLIDE_INDEX_PLH, slideIndex))
    }
  }

  function fmtEmbedTmpl(root_location, withSlideIndex = false) {
    const parsedRoot = memParseUrl(root_location)
    const baseSearch = parsedRoot.search
      .replace(EMBED_ITEM_RE, '')
      .replace(EMBED_SLIDE_RE, '')
      .replace(/^&/, '?')
    const postSearch = isEmpty(baseSearch)
      ? '?' + EMBED_ITEM_KEY + '=' + POST_KEY_PLH
      : '&' + EMBED_ITEM_KEY + '=' + POST_KEY_PLH
    const slideSearch = '&' + EMBED_SLIDE_INDEX + '=' + SLIDE_INDEX_PLH

    const base =
        parsedRoot.origin
      + parsedRoot.pathname
      + baseSearch
      + postSearch
      + (withSlideIndex ? slideSearch : '')
      + parsedRoot.hash

    return base
  }

  // FmtSmartLink
  function FmtSmartLink(base_url, root_location, key) {
    const slideTmpl = fmtSmartTmpl(base_url, root_location, key, true)
    const plainTmpl = fmtSmartTmpl(base_url, root_location, key)

    return (postKey, slideIndex) => {
      const tmpl = slideIndex !== 0
        ? slideTmpl
        : plainTmpl

      return toUrl(tmpl.replace(POST_KEY_PLH, postKey).replace(SLIDE_INDEX_PLH, slideIndex))
    }
  }

  function fmtSmartTmpl(base_url, root_location, key, withSlideIndex = false) {
    const parsedRoot = memParseUrl(root_location)
    const parsedBase = memParseUrl(base_url)
    const baseSearch = parsedRoot.search
      .replace(EMBED_ITEM_RE, '')
      .replace(/^&/, '?')

    const clearedRoot =
        parsedRoot.origin
      + parsedRoot.pathname
      + baseSearch
      + parsedRoot.hash
    const encodedRoot = window.encodeURIComponent(clearedRoot)

    const base =
        parsedBase.origin
      + '/' + SMART_PATH_SIGN + '/' + key
      + '/' + POST_KEY_PLH
      + (withSlideIndex ? '/' + SLIDE_INDEX_PLH : '')
      + '?' + SMART_ROOT_KEY + '=' + encodedRoot

    return base
  }

  // FmtGleamLink
  function FmtGleamLink(base_url, root_location, key) {
    const slideTmpl = fmtGleamTmpl(base_url, root_location, key, true)
    const plainTmpl = fmtGleamTmpl(base_url, root_location, key)

    return (postKey, slideIndex) => {
      const tmpl = slideIndex !== 0
        ? slideTmpl
        : plainTmpl

      return toUrl(tmpl.replace(POST_KEY_PLH, postKey).replace(SLIDE_INDEX_PLH, slideIndex))
    }
  }

  function fmtGleamTmpl(base_url, root_location, key, withSlideIndex = false) {
    const expPathRE = RegExp('^' + escapeRegExp(`/${GLEAM_PATH_SIGN}/${key}`))
    const parsedBase = memParseUrl(base_url)
    const parsedRoot = memParseUrl(root_location)

    const rootLooksLikeHostedGallery = true
      && parsedRoot.origin === parsedBase.origin
      && expPathRE.test(parsedRoot.pathname)

    if (rootLooksLikeHostedGallery) {
      const base =
          parsedBase.origin
        + '/' + GLEAM_PATH_SIGN + '/' + key
        + '/' + POST_KEY_PLH
        + (withSlideIndex ? '/' + SLIDE_INDEX_PLH : '')
        + parsedRoot.search
        + parsedRoot.hash
      return base
    } else {
      const base =
          parsedBase.origin
        + '/' + GLEAM_PATH_SIGN + '/' + key
        + '/' + POST_KEY_PLH
        + (withSlideIndex ? '/' + SLIDE_INDEX_PLH : '')
      return base
    }
  }

  // FmtFacebookLink
  function FmtFacebookLink(base_url, key) {
    const slideTmpl = fmtFacebookTmpl(base_url, key, true)
    const plainTmpl = fmtFacebookTmpl(base_url, key)

    return (postKey, slideIndex) => {
      const tmpl = slideIndex !== 0
        ? slideTmpl
        : plainTmpl

      return toUrl(tmpl.replace(POST_KEY_PLH, postKey).replace(SLIDE_INDEX_PLH, slideIndex))
    }
  }

  function fmtFacebookTmpl(base_url, key, withSlideIndex = false) {
    const parsedBase = memParseUrl(base_url)
    const base =
        parsedBase.origin
      + '/' + FACEBOOK_PATH_SIGN + '/' + key
      + '?' + EMBED_ITEM_KEY + '=' + POST_KEY_PLH
      + (withSlideIndex ? '&' + EMBED_SLIDE_INDEX + '=' + SLIDE_INDEX_PLH : '')

    return base
  }

  // export
  return {
    fmtText,
    fmtCode,
    fmtLink
  }
}
